import Notify from './notifyActions'
import { GET_APP_SETTING, GET_APP_SETTING_SUCCESS } from '../reducers/appSettingReducer'
import type { AppSettingsResponse } from '../../types/generated/models/app-settings-response'
import type { ReduxAxiosAction, AppDispatch, GetState, ThunkResult } from './common'

export const APP_SETTINGS_UPDATE_SUCCESSFUL_TOAST = 'success_saving_settings'
export const APP_SETTINGS_UPDATE_FAILURE_TOAST = 'error_saving_settings'

const APP_SETTINGS_CACHE_TTL_MS = 5 * 60 * 1000

interface GetAppSettingOptions {
  force?: boolean
}

const createGetAppSettingRequest = (loadedForCustomerId: number | string | null): ReduxAxiosAction => ({
  type: GET_APP_SETTING,
  meta: {
    loadedAt: Date.now(),
    loadedForCustomerId,
  },
  payload: {
    request: {
      mode: 'no-cors',
      url: 'app-settings',
      method: 'GET'
    }
  }
})

const getCachedAppSettingsResponse = (state: any): AppSettingsResponse | null => {
  return state?.appSettingReducer?.lastResponse ?? null
}

const shouldUseCachedAppSettings = (state: any, force?: boolean): boolean => {
  if (force) {
    return false
  }

  const cachedResponse = getCachedAppSettingsResponse(state)
  const lastLoadedAt = state?.appSettingReducer?.lastLoadedAt
  const loadedForCustomerId = state?.appSettingReducer?.loadedForCustomerId ?? null
  const currentCustomerId = state?.profileReducer?.profile?.customerid ?? null

  if (!cachedResponse || !lastLoadedAt) {
    return false
  }

  if (loadedForCustomerId !== currentCustomerId) {
    return false
  }

  return Date.now() - lastLoadedAt < APP_SETTINGS_CACHE_TTL_MS
}

export const getAppSetting = (options: GetAppSettingOptions = {}): ThunkResult => {
  return (dispatch: AppDispatch, getState?: GetState) => {
    const state = getState ? getState() : null

    if (state && shouldUseCachedAppSettings(state, options.force)) {
      return Promise.resolve({
        type: GET_APP_SETTING_SUCCESS,
        payload: {
          data: getCachedAppSettingsResponse(state)
        }
      })
    }

    const currentCustomerId = state?.profileReducer?.profile?.customerid ?? null
    return dispatch(createGetAppSettingRequest(currentCustomerId))
  }
}

export const UPDATE_FILE_UPLOAD_SETTINGS = 'UPDATE_FILE_UPLOAD_SETTINGS'
export const updateFileUploadSettings = (data: any): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return new Promise<void>((resolve, reject) => {
      dispatch({
        type: UPDATE_FILE_UPLOAD_SETTINGS,
        payload: {
          request: {
            mode: 'no-cors',
            url: 'app-settings/file-upload-settings',
            method: 'PUT',
            data: { request: data }
          }
        }
      }).then((response: any) => {
        if (response.payload.data.success) {
          dispatch(Notify.success(APP_SETTINGS_UPDATE_SUCCESSFUL_TOAST))
          resolve()
        } else {
          reject(response.payload.data)
        }
      }).catch((error: any) => {
        dispatch(Notify.error(APP_SETTINGS_UPDATE_FAILURE_TOAST))
        reject(error)
      })
    })
  }
}

export const UPDATE_ACCOUNT_LOCKOUT_SETTINGS = 'UPDATE_ACCOUNT_LOCKOUT_SETTINGS'
export const updateAccountLockoutSettings = (data: any): ThunkResult => {
  return (dispatch: AppDispatch) => {
    return new Promise<void>((resolve, reject) => {
      dispatch({
        type: UPDATE_ACCOUNT_LOCKOUT_SETTINGS,
        payload: {
          request: {
            mode: 'no-cors',
            url: 'app-settings/account-lockout',
            method: 'PUT',
            data: { request: data }
          }
        }
      }).then((response: any) => {
        if (response.payload.data.success) {
          dispatch(Notify.success(APP_SETTINGS_UPDATE_SUCCESSFUL_TOAST))
          resolve()
        } else {
          reject(response.payload.data)
        }
      }).catch((error: any) => {
        dispatch(Notify.error(APP_SETTINGS_UPDATE_FAILURE_TOAST))
        reject(error)
      })
    })
  }
}
